<template>
  <div class="priority-filter-container">
    <div class="filter-group">
      <label>{{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.INBOX') }}</label>
      <select v-model="filters.inboxId" :disabled="disabled">
        <option :value="null">
          {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.ALL') }}
        </option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label>
        {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.TEAM') }}
      </label>
      <select v-model="filters.teamId" :disabled="disabled">
        <option :value="null">
          {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.ALL') }}
        </option>
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label>
        {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.AGENT') }}
      </label>
      <select
        v-model="filters.agentId"
        :disabled="disabled"
      >
        <option :value="null">
          {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.ALL') }}
        </option>
        <option v-for="user in selectableUsers" :key="user.id" :value="user.id">
          {{ user.name }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label>
        {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.AUTO_CANCELLATION') }}
      </label>
      <select v-model="filters.enableCancellation" :disabled="disabled">
        <option :value="null">
          {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.ALL') }}
        </option>

        <option :value="true">
          {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.AUTO_CANCELLATION.YES') }}
        </option>

        <option :value="false">
          {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.AUTO_CANCELLATION.NO') }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label>
        {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.CONVERSATION_STATUS') }}
      </label>

      <select v-model="filters.conversationStatus" :disabled="disabled">
        <option :value="null">
          {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.ALL') }}
        </option>

        <option value="open">
          {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.CONVERSATION_STATUS.OPEN') }}
        </option>

        <option value="resolved">
          {{
            $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.CONVERSATION_STATUS.RESOLVED')
          }}
        </option>

        <option value="bot">
          {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.CONVERSATION_STATUS.BOT') }}
        </option>

        <option value="snoozed">
          {{
            $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.CONVERSATION_STATUS.SNOOZED')
          }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label>
        {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.PERIOD') }}
      </label>

      <div class="date-range">
        <input
          ref="startDate"
          v-model="filters.startDate"
          type="date"
          :min="minDate"
          :max="maxDate"
          :disabled="disabled"
          @focus="$event.target.showPicker && $event.target.showPicker()"
        />

        <span>{{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.UNTIL') }}</span>

        <input
          ref="endDate"
          v-model="filters.endDate"
          type="date"
          :min="minDate"
          :max="maxDate"
          :disabled="disabled"
          @focus="$event.target.showPicker && $event.target.showPicker()"
        />
      </div>
    </div>

    <div class="button-wrapper">
      <button class="apply-button" @click="applyFilters" :disabled="disabled">
        {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.APPLY') }}
      </button>

      <button
        class="apply-button clear-button"
        @click="clearFilters"
        :disabled="disabled"
      >
        {{ $t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.CLEAR') }}
      </button>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';

export default {
  mixins: [alertMixin],
  name: 'UpcomingScheduledMessagesFilter',
  props: {
    paginatedList: {
      type: Array,
      required: true,
    },
    availableFiltersData: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    initialFilters: {
      type: Object,
      required: true,
    },
    isAgentFilterLocked: {
      type: Boolean,
      default: false,
    },
    restrictedAgentId: {
      type: [Number, String],
      default: null,
    },
    restrictedAgentName: {
      type: String,
      default: '',
    },
  },
  data() {
    const today = new Date();

    const endDate = new Date();
    endDate.setDate(today.getDate() + 7);

    return {
      filters: {
        inboxId: null,
        teamId: null,
        agentId: null,
        enableCancellation: null,
        conversationStatus: null,
        startDate: this.getLocalDate(today),
        endDate: this.getLocalDate(endDate),
      },
    };
  },
  computed: {
    inboxes() {
      return (this.availableFiltersData.inboxes || []).map(i => ({
        id: i.id,
        name: this.truncateName(i.name),
      }));
    },
    teams() {
      return (this.availableFiltersData.teams || []).map(t => ({
        id: t.id,
        name: this.truncateName(t.name),
      }));
    },
    users() {
      return (this.availableFiltersData.users || []).map(u => ({
        id: u.id,
        name: this.truncateName(u.name),
      }));
    },
    restrictedAgentOption() {
      if (!this.restrictedAgentId) {
        return null;
      }

      const restrictedUser = this.users.find(
        user => String(user.id) === String(this.restrictedAgentId)
      );

      if (restrictedUser) {
        return restrictedUser;
      }

      return {
        id: this.restrictedAgentId,
        name: this.truncateName(this.restrictedAgentName),
      };
    },
    selectableUsers() {
      if (!this.isAgentFilterLocked) {
        return this.users;
      }

      return this.restrictedAgentOption ? [this.restrictedAgentOption] : [];
    },
    minDate() {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      return this.getLocalDate(now);
    },
    maxDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 1);

      return date.toISOString().split('T')[0];
    },
  },
  watch: {
    'filters.startDate'(value) {
      if (!value) return;

      if (value < this.minDate) {
        this.filters.startDate = this.minDate;
      }

      if (value > this.maxDate) {
        this.filters.startDate = this.maxDate;
      }

      if (this.filters.endDate && this.filters.endDate < value) {
        this.filters.endDate = value;
      }
    },
    'filters.endDate'(value) {
      if (!value) return;

      if (value < this.minDate) {
        this.filters.endDate = this.minDate;
      }

      if (value > this.maxDate) {
        this.filters.endDate = this.maxDate;
      }
    },
    initialFilters: {
      handler(newVal) {
        const defaults = this.getDefaultDateRange();

        this.filters = {
          ...newVal,
          startDate: newVal.startDate || defaults.startDate,
          endDate: newVal.endDate || defaults.endDate,
        };
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    truncateName(name, limit = 20) {
      if (!name) return '---';

      return name.length > limit ? `${name.slice(0, limit - 3)}...` : name;
    },
    applyFilters() {
      if (
        (!!this.filters.startDate && !this.filters.endDate) ||
        (!this.filters.startDate && !!this.filters.endDate)
      ) {
        this.showAlert(
          this.$t('UPCOMING_SCHEDULED_MESSAGES_PAGE.FILTERS.INVALID_PERIOD')
        );
        return;
      }

      this.$emit('filter-applied', this.filters);
    },
    clearFilters() {
      const defaults = this.getDefaultDateRange();

      this.filters = {
        inboxId: null,
        teamId: null,
        agentId: this.isAgentFilterLocked ? this.initialFilters.agentId : null,
        enableCancellation: null,
        conversationStatus: null,
        startDate: defaults.startDate,
        endDate: defaults.endDate,
      };

      this.$emit('filter-applied', this.filters);
    },
    getDefaultDateRange() {
      const today = new Date();

      const endDate = new Date();
      endDate.setDate(today.getDate() + 7);

      return {
        startDate: this.getLocalDate(today),
        endDate: this.getLocalDate(endDate),
      };
    },
    getLocalDate(date = new Date()) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped>
.priority-filter-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
  padding-left: 1.7rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  min-width: 160px;
}

.filter-group select {
  height: 36px;
  padding: 0.25rem 0.5rem;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.apply-button {
  height: 36px;
  padding: 0 1rem;
  background-color: #0041c2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 5px;
  margin-right: 0.5rem;
}

.clear-button {
  background-color: #888;
}

.apply-button:disabled,
.clear-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-wrapper {
  display: flex;
  align-self: flex-start;
  padding-top: 20px;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 20;
}

.date-range input {
  height: 36px;
  padding: 0.25rem 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  position: relative;
  z-index: 20;
}
</style>
